import { Component, computed, inject, input, model, signal, viewChild } from '@angular/core';
import { ColDef, GridOptions } from 'ag-grid-community';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { finalize } from 'rxjs';
import { PanModalComponent } from '../../../../components/pan-modal/pan-modal.component';
import { defaultTableDefOptions } from '../../../../helpers';
import { EdgeDeviceService } from '../../../../service/edge-device.service';
import { LocationService } from '../../../../service/location.service';
import { NotifyService } from '../../../../service/notify.service';
import { Branch, Location, LocationCamera, LocationResponse } from '../../../../types';
import { EdgeDevice, EVENT_FORMAT, EventFormat, PanoBridgeProtocol } from '../../../../types/edge-device';
import { CellInstallerDashboardShoppersenseComponent } from '../cell-installer-dashboard-shoppersense/cell-installer-dashboard-shoppersense.component';

@Component({
  selector: 'app-installer-dashboard-table-panobridge',
  standalone: false,
  templateUrl: './installer-dashboard-table-panobridge.component.html',
  styleUrl: './installer-dashboard-table-panobridge.component.scss',
})
export class InstallerDashboardTablePanobridgeComponent {
  private edgeDeviceService = inject(EdgeDeviceService);
  private notifyService = inject(NotifyService);
  private locationService = inject(LocationService);

  public branchId = input.required<number>();
  public panobridges = model.required<EdgeDevice[]>();

  public queryParams = injectQueryParams();

  public newPanobridgeModal = viewChild<PanModalComponent>('newPanobridgeModal');

  public isLoadingLocations = signal<boolean>(false);
  public locations = signal<LocationResponse[]>([]);

  public cameras = signal<LocationCamera[]>([]);

  public formatOptions: EventFormat[] = Object.values(EVENT_FORMAT);
  public newPanobridgeSerialNumber = signal<string>('');
  public newPanobridgeLocation = signal<Location | undefined>(undefined);
  public newPanobridgeBranch = signal<Branch | undefined>(undefined);
  public newPanobridgeEventFormat = signal<EventFormat>('ISS45');
  public disableProtocolSelection = computed(() => this.newPanobridgeEventFormat() === 'RORC_CSV');
  public newPanobridgePort = signal<number>(5005);
  public newPanobridgeProtocol = signal<PanoBridgeProtocol>('tcp');

  public deviceId = computed(() => this.queryParams().device_id);

  public readonly panobridgeColumns: ColDef<EdgeDevice>[] = [
    {
      field: 'serial_number',
      headerName: 'Serial Number',
      cellDataType: 'text',
      minWidth: 200,
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
    },
    {
      field: 'location.name',
      headerName: 'Location',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
      tooltipField: 'location.name',
    },
    {
      field: 'config.pano_bridge.protocol',
      headerName: 'Protocol',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      field: 'config.pano_bridge.port',
      headerName: 'Port',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      headerName: '',
      suppressColumnsToolPanel: true,
      suppressKeyboardEvent: () => true,
      sortable: false,
      width: 280,
      maxWidth: 280,
      cellClass: ['inline-flex', 'items-center', 'justify-end'],
      cellRenderer: CellInstallerDashboardShoppersenseComponent,
      cellRendererParams: {
        branchId: () => this.branchId(),
        deleteCallback: (element: EdgeDevice) => this.removePanobridge(element),
      },
    },
  ];

  public readonly panobridgeTable: GridOptions<EdgeDevice> = {
    ...defaultTableDefOptions(),
    pagination: true,
    paginationPageSize: 20,
    paginationPageSizeSelector: false,
  };

  public loadLocations() {
    if (!this.newPanobridgeBranch()) return;

    this.isLoadingLocations.set(true);
    this.locationService
      .getAllLocations({ branch_ids: [this.newPanobridgeBranch()?.id] })
      .pipe(finalize(() => this.isLoadingLocations.set(false)))
      .subscribe((locationRes) => {
        this.locations.set(locationRes.items);
      });
  }

  public addPanobridge() {
    this.edgeDeviceService
      .connectPanoBridgeEdgeDevice(this.deviceId() ?? this.newPanobridgeSerialNumber(), {
        location_id: this.newPanobridgeLocation()!.id,
        port: this.newPanobridgePort(),
        protocol: this.newPanobridgeProtocol(),
        format: this.newPanobridgeEventFormat(),
      })
      .subscribe((device) => {
        this.panobridges.update((panobridges) => [...panobridges, device]);
        this.notifyService.notify('PanoBridge added successfully!');
      });
  }

  public removePanobridge(device: EdgeDevice) {
    this.edgeDeviceService.disconnectPanoBridgeEdgeDevice(device.id).subscribe((removedDevice) => {
      this.panobridges.update((panobridges) => panobridges.filter((s) => s.id !== removedDevice.id));
      this.notifyService.notify('PanoBridge removed successfully!');
    });
  }

  public forceUdp() {
    if (this.newPanobridgeEventFormat() !== 'RORC_CSV') return;
    this.newPanobridgeProtocol.set('udp');
  }
}
